// How a tour was asked to start:
// - `auto`: the user reached a screen with this tour; it plays only if a
//   campaign targets it for this user (see `campaigns/`);
// - `explicit`: the user clicked "Show me" on a release entry: always plays;
// - `preview`: an admin replays it (`?tour_preview=<id>`, the What's new admin
//   bar): always plays, records nothing.
export type TourTrigger = 'auto' | 'explicit' | 'preview';

// Steps that can be shown, in order: their element is on screen, or the
// screen can reveal it (`useTourReveal`). A tour with none left is not started.
export const resolveTourSteps = <Step extends { anchor: string }>(
  steps: readonly Step[],
  isAvailable: (anchor: string) => boolean,
): Step[] => steps.filter(step => isAvailable(step.anchor));

// How "Show me" starts a tour. The screen that hosts it reads the start
// request from the router state when it mounts (`useAutoTour`). A host that is
// already mounted never re-reads it: the top-bar bell, the app layout, or the
// same screen for another record. Such a tour is started directly, after
// opening its screen if that is another page; without router state, so going
// back to that page does not replay it.
export type OpenTourPlan =
  | { kind: 'navigate'; path: string }
  | { kind: 'start'; path: string | null };

export const planOpenTour = ({
  target,
  current,
  hostMounted,
}: {
  target: string;
  current: string;
  hostMounted: boolean;
}): OpenTourPlan =>
  hostMounted
    ? { kind: 'start', path: target === current ? null : target }
    : { kind: 'navigate', path: target };
